import type { FieldMemo, MemoAuthorType, MemoType } from '../types/fieldMemo';
import { apiClient } from './api/apiClient';

/**
 * API レスポンス型。日付は ISO 文字列として返る。
 */
interface MemoApiResponse {
  id: number;
  type: MemoType;
  entityId: number;
  userId: number;
  userName: string;
  authorType: MemoAuthorType;
  content: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * API レスポンスを FieldMemo に変換する。
 *
 * @param res API レスポンス
 * @returns FieldMemo
 */
function toFieldMemo(res: MemoApiResponse): FieldMemo {
  return {
    id: String(res.id),
    type: res.type,
    entityId: String(res.entityId),
    userId: String(res.userId),
    userName: res.userName,
    authorType: res.authorType,
    content: res.content,
    createdAt: new Date(res.createdAt),
    updatedAt: new Date(res.updatedAt),
  };
}

/**
 * メモサービス。
 *
 * バックエンド `/api/v1/memos` エンドポイントを呼び出し、汎用メモの取得・作成を提供する。
 */
class MemoService {
  /**
   * 対象エンティティに紐づくメモ一覧を作成日時降順で取得する。
   *
   * @param type メモ種別
   * @param entityId 対象エンティティ ID
   * @returns FieldMemo の配列（createdAt 降順）
   */
  async list(type: MemoType, entityId: string): Promise<FieldMemo[]> {
    const memos = await apiClient.get<MemoApiResponse[]>('/memos', {
      type,
      entityId,
    });
    return memos.map(toFieldMemo);
  }

  /**
   * メモを作成する。
   *
   * @param type メモ種別
   * @param entityId 対象エンティティ ID
   * @param content 本文
   * @returns 作成した FieldMemo
   */
  async create(type: MemoType, entityId: string, content: string): Promise<FieldMemo> {
    const res = await apiClient.post<MemoApiResponse>('/memos', {
      type,
      entityId: Number(entityId),
      content,
    });
    return toFieldMemo(res);
  }
}

/** メモサービスのシングルトンインスタンス */
export const memoService = new MemoService();
